import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';
import vocabularyService, {
    CreateWordRequest,
    UpdateWordRequest,
    VocabularyFilters,
    PaginationParams,
    CreateFlashcardGroupRequest,
    UpdateFlashcardGroupRequest,
    FlashcardGroupFilters,
    PracticeResult,
    CreateVocabularyGroupRequest,
    UpdateVocabularyGroupRequest,
    VocabularyGroupFilters,
    CreateVocabularySetRequest,
    UpdateVocabularySetRequest,
    VocabularySetFilters,
    CreateStudySessionRequest,
    UpdateStudySessionRequest,
    CompleteStudySessionRequest,
    SubmitVocabularyAnswerRequest,
    CreateGroupCollectionRequest,
    UpdateGroupCollectionRequest,
    SavedWordFilters,
    CreateSavedWordRequest,
    UpdateSavedWordRequest,
    MinigameSession,
    CreateMinigameSessionRequest,
    UpdateMinigameSessionRequest,
    UpdateStudyProgressRequest,
} from '@/services/vocabularyService';
import {
    // Words
    fetchWordsRequest,
    fetchWordsSuccess,
    fetchWordsFailure,
    fetchWordByIdRequest,
    fetchWordByIdSuccess,
    fetchWordByIdFailure,
    createWordRequest,
    createWordSuccess,
    createWordFailure,
    createBulkWordsRequest,
    createBulkWordsSuccess,
    createBulkWordsFailure,
    updateWordRequest,
    updateWordSuccess,
    updateWordFailure,
    deleteWordRequest,
    deleteWordSuccess,
    deleteWordFailure,
    toggleFavoriteRequest,
    toggleFavoriteSuccess,
    toggleFavoriteFailure,
    toggleBookmarkRequest,
    toggleBookmarkSuccess,
    toggleBookmarkFailure,
    fetchFavoriteWordsRequest,
    fetchFavoriteWordsSuccess,
    fetchFavoriteWordsFailure,
    fetchBookmarkedWordsRequest,
    fetchBookmarkedWordsSuccess,
    fetchBookmarkedWordsFailure,

    // Categories
    fetchCategoriesRequest,
    fetchCategoriesSuccess,
    fetchCategoriesFailure,

    // Enhanced Vocabulary Groups
    fetchVocabularyGroupsRequest,
    fetchVocabularyGroupsSuccess,
    fetchVocabularyGroupsFailure,
    fetchVocabularyGroupByIdRequest,
    fetchVocabularyGroupByIdSuccess,
    fetchVocabularyGroupByIdFailure,
    createVocabularyGroupRequest,
    createVocabularyGroupSuccess,
    createVocabularyGroupFailure,
    updateVocabularyGroupRequest,
    updateVocabularyGroupSuccess,
    updateVocabularyGroupFailure,
    deleteVocabularyGroupRequest,
    deleteVocabularyGroupSuccess,
    deleteVocabularyGroupFailure,
    addWordsToVocabularyGroupRequest,
    addWordsToVocabularyGroupSuccess,
    addWordsToVocabularyGroupFailure,
    removeWordsFromVocabularyGroupRequest,
    removeWordsFromVocabularyGroupSuccess,
    removeWordsFromVocabularyGroupFailure,
    fetchWordsInVocabularyGroupRequest,
    fetchWordsInVocabularyGroupSuccess,
    fetchWordsInVocabularyGroupFailure,

    // Group Sharing
    shareVocabularyGroupRequest,
    shareVocabularyGroupSuccess,
    shareVocabularyGroupFailure,
    fetchGroupSharesRequest,
    fetchGroupSharesSuccess,
    fetchGroupSharesFailure,

    // Vocabulary Sets
    fetchVocabularySetsRequest,
    fetchVocabularySetsSuccess,
    fetchVocabularySetsFailure,
    fetchVocabularySetByIdRequest,
    fetchVocabularySetByIdSuccess,
    fetchVocabularySetByIdFailure,
    createVocabularySetRequest,
    createVocabularySetSuccess,
    createVocabularySetFailure,
    updateVocabularySetRequest,
    updateVocabularySetSuccess,
    updateVocabularySetFailure,
    deleteVocabularySetRequest,
    deleteVocabularySetSuccess,
    deleteVocabularySetFailure,

    // Study Sessions
    createStudySessionRequest,
    createStudySessionSuccess,
    createStudySessionFailure,
    updateStudySessionRequest,
    updateStudySessionSuccess,
    updateStudySessionFailure,
    fetchStudySessionsRequest,
    fetchStudySessionsSuccess,
    fetchStudySessionsFailure,
    completeStudySessionRequest,
    completeStudySessionSuccess,
    completeStudySessionFailure,
    submitVocabularyAnswerRequest,
    submitVocabularyAnswerSuccess,
    submitVocabularyAnswerFailure,

    // Group Collections
    fetchGroupCollectionsRequest,
    fetchGroupCollectionsSuccess,
    fetchGroupCollectionsFailure,
    createGroupCollectionRequest,
    createGroupCollectionSuccess,
    createGroupCollectionFailure,
    updateGroupCollectionRequest,
    updateGroupCollectionSuccess,
    updateGroupCollectionFailure,
    deleteGroupCollectionRequest,
    deleteGroupCollectionSuccess,
    deleteGroupCollectionFailure,

    // Legacy Flashcard Groups
    fetchFlashcardGroupsRequest,
    fetchFlashcardGroupsSuccess,
    fetchFlashcardGroupsFailure,
    fetchFlashcardGroupByIdRequest,
    fetchFlashcardGroupByIdSuccess,
    fetchFlashcardGroupByIdFailure,
    createFlashcardGroupRequest,
    createFlashcardGroupSuccess,
    createFlashcardGroupFailure,
    updateFlashcardGroupRequest,
    updateFlashcardGroupSuccess,
    updateFlashcardGroupFailure,
    deleteFlashcardGroupRequest,
    deleteFlashcardGroupSuccess,
    deleteFlashcardGroupFailure,
    addWordsToGroupRequest,
    addWordsToGroupSuccess,
    addWordsToGroupFailure,
    removeWordsFromGroupRequest,
    removeWordsFromGroupSuccess,
    removeWordsFromGroupFailure,
    fetchWordsInGroupRequest,
    fetchWordsInGroupSuccess,
    fetchWordsInGroupFailure,

    // Practice
    practiceWordRequest,
    practiceWordSuccess,
    practiceWordFailure,
    submitPracticeResultRequest,
    submitPracticeResultSuccess,
    submitPracticeResultFailure,
    fetchPracticeHistoryRequest,
    fetchPracticeHistorySuccess,
    fetchPracticeHistoryFailure,

    // Vocabulary Categories
    fetchVocabularyCategoriesRequest,
    fetchVocabularyCategoriesSuccess,
    fetchVocabularyCategoriesFailure,

    // Enhanced Word Saving
    fetchSavedWordsRequest,
    fetchSavedWordsSuccess,
    fetchSavedWordsFailure,
    saveWordRequest,
    saveWordSuccess,
    saveWordFailure,
    updateSavedWordRequest,
    updateSavedWordSuccess,
    updateSavedWordFailure,
    removeSavedWordRequest,
    removeSavedWordSuccess,
    removeSavedWordFailure,
    bulkSaveWordsRequest,
    bulkSaveWordsSuccess,
    bulkSaveWordsFailure,
    bulkRemoveSavedWordsRequest,
    bulkRemoveSavedWordsSuccess,
    bulkRemoveSavedWordsFailure,

    // Minigame Sessions
    createMinigameSessionRequest,
    createMinigameSessionSuccess,
    createMinigameSessionFailure,
    updateMinigameSessionRequest,
    updateMinigameSessionSuccess,
    updateMinigameSessionFailure,
    fetchMinigameSessionsRequest,
    fetchMinigameSessionsSuccess,
    fetchMinigameSessionsFailure,
    fetchMinigameStatsRequest,
    fetchMinigameStatsSuccess,
    fetchMinigameStatsFailure,
    generateMinigameWordsRequest,
    generateMinigameWordsSuccess,
    generateMinigameWordsFailure,

    // Progress Tracking
    updateStudyProgressRequest,
    updateStudyProgressSuccess,
    updateStudyProgressFailure,
    fetchStudyProgressRequest,
    fetchStudyProgressSuccess,
    fetchStudyProgressFailure,
    fetchUserStudyProgressRequest,
    fetchUserStudyProgressSuccess,
    fetchUserStudyProgressFailure,
    fetchWordsForReviewRequest,
    fetchWordsForReviewSuccess,
    fetchWordsForReviewFailure,

    // Enhanced Group Management
    reorderWordsInGroupRequest,
    reorderWordsInGroupSuccess,
    reorderWordsInGroupFailure,
    moveWordsBetweenGroupsRequest,
    moveWordsBetweenGroupsSuccess,
    moveWordsBetweenGroupsFailure,
    duplicateGroupRequest,
    duplicateGroupSuccess,
    duplicateGroupFailure,

    // Session Integration
    linkWordsToSessionRequest,
    linkWordsToSessionSuccess,
    linkWordsToSessionFailure,
    fetchSessionVocabularyRequest,
    fetchSessionVocabularySuccess,
    fetchSessionVocabularyFailure,
    updateSessionVocabularyProgressRequest,
    updateSessionVocabularyProgressSuccess,
    updateSessionVocabularyProgressFailure,
    fetchTranscriptDataRequest,
    fetchTranscriptDataSuccess,
    fetchTranscriptDataFailure,
} from '../slices/vocabulary';

// Vocabulary Words Worker Sagas
function* fetchWordsWorker(
    action: PayloadAction<{ filters?: VocabularyFilters; pagination?: PaginationParams }>,
): SagaIterator {
    try {
        const { filters, pagination } = action.payload;
        const response = yield call(vocabularyService.getWords, filters, pagination);
        yield put(fetchWordsSuccess(response));
    } catch (error: any) {
        yield put(fetchWordsFailure(error.response?.data?.message || 'Failed to fetch words'));
    }
}

function* fetchWordByIdWorker(action: PayloadAction<string>): SagaIterator {
    try {
        const word = yield call(vocabularyService.getWordById, action.payload);
        yield put(fetchWordByIdSuccess(word));
    } catch (error: any) {
        yield put(fetchWordByIdFailure(error.response?.data?.message || 'Failed to fetch word'));
    }
}

function* createWordWorker(action: PayloadAction<CreateWordRequest>): SagaIterator {
    try {
        const word = yield call(vocabularyService.createWord, action.payload);
        yield put(createWordSuccess(word));
    } catch (error: any) {
        yield put(createWordFailure(error.response?.data?.message || 'Failed to create word'));
    }
}

function* createBulkWordsWorker(action: PayloadAction<CreateWordRequest[]>): SagaIterator {
    try {
        const words = yield call(vocabularyService.createBulkWords, action.payload);
        yield put(createBulkWordsSuccess(words));
    } catch (error: any) {
        yield put(createBulkWordsFailure(error.response?.data?.message || 'Failed to create words'));
    }
}

function* updateWordWorker(action: PayloadAction<UpdateWordRequest>): SagaIterator {
    try {
        const word = yield call(vocabularyService.updateWord, action.payload);
        yield put(updateWordSuccess(word));
    } catch (error: any) {
        yield put(updateWordFailure(error.response?.data?.message || 'Failed to update word'));
    }
}

function* deleteWordWorker(action: PayloadAction<string>): SagaIterator {
    try {
        yield call(vocabularyService.deleteWord, action.payload);
        yield put(deleteWordSuccess(action.payload));
    } catch (error: any) {
        yield put(deleteWordFailure(error.response?.data?.message || 'Failed to delete word'));
    }
}

function* toggleFavoriteWorker(action: PayloadAction<string>): SagaIterator {
    try {
        const word = yield call(vocabularyService.toggleFavorite, action.payload);
        yield put(toggleFavoriteSuccess(word));
    } catch (error: any) {
        yield put(toggleFavoriteFailure(error.response?.data?.message || 'Failed to toggle favorite'));
    }
}

function* toggleBookmarkWorker(action: PayloadAction<string>): SagaIterator {
    try {
        const word = yield call(vocabularyService.toggleBookmark, action.payload);
        yield put(toggleBookmarkSuccess(word));
    } catch (error: any) {
        yield put(toggleBookmarkFailure(error.response?.data?.message || 'Failed to toggle bookmark'));
    }
}

function* fetchFavoriteWordsWorker(action: PayloadAction<PaginationParams | undefined>): SagaIterator {
    try {
        const response = yield call(vocabularyService.getFavoriteWords, action.payload);
        yield put(fetchFavoriteWordsSuccess(response));
    } catch (error: any) {
        yield put(fetchFavoriteWordsFailure(error.response?.data?.message || 'Failed to fetch favorite words'));
    }
}

function* fetchBookmarkedWordsWorker(action: PayloadAction<PaginationParams | undefined>): SagaIterator {
    try {
        const response = yield call(vocabularyService.getBookmarkedWords, action.payload);
        yield put(fetchBookmarkedWordsSuccess(response));
    } catch (error: any) {
        yield put(fetchBookmarkedWordsFailure(error.response?.data?.message || 'Failed to fetch bookmarked words'));
    }
}

// Categories Worker Sagas
function* fetchCategoriesWorker(): SagaIterator {
    try {
        const categories = yield call(vocabularyService.getCategories);
        yield put(fetchCategoriesSuccess(categories));
    } catch (error: any) {
        yield put(fetchCategoriesFailure(error.response?.data?.message || 'Failed to fetch categories'));
    }
}

// Enhanced Vocabulary Groups Worker Sagas
function* fetchVocabularyGroupsWorker(action: PayloadAction<any>): SagaIterator {
    try {
        const response = yield call(vocabularyService.getVocabularyGroups, action.payload);
        yield put(fetchVocabularyGroupsSuccess(response));
    } catch (error: any) {
        yield put(fetchVocabularyGroupsFailure(error.response?.data?.message || 'Failed to fetch vocabulary groups'));
    }
}

function* fetchVocabularyGroupByIdWorker(action: PayloadAction<string>): SagaIterator {
    try {
        const group = yield call(vocabularyService.getVocabularyGroupById, action.payload);
        yield put(fetchVocabularyGroupByIdSuccess(group));
    } catch (error: any) {
        yield put(fetchVocabularyGroupByIdFailure(error.response?.data?.message || 'Failed to fetch vocabulary group'));
    }
}

function* createVocabularyGroupWorker(action: PayloadAction<CreateVocabularyGroupRequest>): SagaIterator {
    try {
        const response = yield call(vocabularyService.createVocabularyGroup, action.payload);
        yield put(createVocabularyGroupSuccess(response));
    } catch (error: any) {
        yield put(createVocabularyGroupFailure(error.response?.data?.message || 'Failed to create vocabulary group'));
    }
}

function* updateVocabularyGroupWorker(action: PayloadAction<UpdateVocabularyGroupRequest>): SagaIterator {
    try {
        const response = yield call(vocabularyService.updateVocabularyGroup, action.payload);
        yield put(updateVocabularyGroupSuccess(response));
    } catch (error: any) {
        yield put(updateVocabularyGroupFailure(error.response?.data?.message || 'Failed to update vocabulary group'));
    }
}

function* deleteVocabularyGroupWorker(action: PayloadAction<string>): SagaIterator {
    try {
        yield call(vocabularyService.deleteVocabularyGroup, action.payload);
        yield put(deleteVocabularyGroupSuccess(action.payload));
    } catch (error: any) {
        yield put(deleteVocabularyGroupFailure(error.response?.data?.message || 'Failed to delete vocabulary group'));
    }
}

function* addWordsToVocabularyGroupWorker(action: PayloadAction<{ groupId: string; wordIds: number[] }>): SagaIterator {
    try {
        const { groupId, wordIds } = action.payload;
        const group = yield call(vocabularyService.addWordsToVocabularyGroup, groupId, wordIds);
        yield put(addWordsToVocabularyGroupSuccess(group));
    } catch (error: any) {
        yield put(addWordsToVocabularyGroupFailure(error.response?.data?.message || 'Failed to add words to group'));
    }
}

function* removeWordsFromVocabularyGroupWorker(
    action: PayloadAction<{ groupId: string; wordIds: number[] }>,
): SagaIterator {
    try {
        const { groupId, wordIds } = action.payload;
        const group = yield call(vocabularyService.removeWordsFromVocabularyGroup, groupId, wordIds);
        yield put(removeWordsFromVocabularyGroupSuccess(group));
    } catch (error: any) {
        yield put(
            removeWordsFromVocabularyGroupFailure(error.response?.data?.message || 'Failed to remove words from group'),
        );
    }
}

function* fetchWordsInVocabularyGroupWorker(
    action: PayloadAction<{ groupId: string; pagination?: PaginationParams }>,
): SagaIterator {
    try {
        const { groupId, pagination } = action.payload;
        const response = yield call(vocabularyService.getWordsInVocabularyGroup, groupId, pagination);
        yield put(fetchWordsInVocabularyGroupSuccess(response));
    } catch (error: any) {
        yield put(
            fetchWordsInVocabularyGroupFailure(error.response?.data?.message || 'Failed to fetch words in group'),
        );
    }
}

// Group Sharing Worker Sagas
function* shareVocabularyGroupWorker(
    action: PayloadAction<{ groupId: string; userId: number; permission: 'view' | 'edit' | 'admin' }>,
): SagaIterator {
    try {
        const { groupId, userId, permission } = action.payload;
        const share = yield call(vocabularyService.shareVocabularyGroup, groupId, userId, permission);
        yield put(shareVocabularyGroupSuccess(share));
    } catch (error: any) {
        yield put(shareVocabularyGroupFailure(error.response?.data?.message || 'Failed to share vocabulary group'));
    }
}

function* fetchGroupSharesWorker(action: PayloadAction<string>): SagaIterator {
    try {
        const shares = yield call(vocabularyService.getGroupShares, action.payload);
        yield put(fetchGroupSharesSuccess(shares));
    } catch (error: any) {
        yield put(fetchGroupSharesFailure(error.response?.data?.message || 'Failed to fetch group shares'));
    }
}

// Vocabulary Sets Worker Sagas
function* fetchVocabularySetsWorker(
    action: PayloadAction<{ filters?: VocabularySetFilters; pagination?: PaginationParams }>,
): SagaIterator {
    try {
        const { filters, pagination } = action.payload;
        const response = yield call(vocabularyService.getVocabularySets, filters, pagination);
        yield put(fetchVocabularySetsSuccess(response));
    } catch (error: any) {
        yield put(fetchVocabularySetsFailure(error.response?.data?.message || 'Failed to fetch vocabulary sets'));
    }
}

function* fetchVocabularySetByIdWorker(action: PayloadAction<string>): SagaIterator {
    try {
        const set = yield call(vocabularyService.getVocabularySetById, action.payload);
        yield put(fetchVocabularySetByIdSuccess(set));
    } catch (error: any) {
        yield put(fetchVocabularySetByIdFailure(error.response?.data?.message || 'Failed to fetch vocabulary set'));
    }
}

function* createVocabularySetWorker(action: PayloadAction<CreateVocabularySetRequest>): SagaIterator {
    try {
        const set = yield call(vocabularyService.createVocabularySet, action.payload);
        yield put(createVocabularySetSuccess(set));
    } catch (error: any) {
        yield put(createVocabularySetFailure(error.response?.data?.message || 'Failed to create vocabulary set'));
    }
}

function* updateVocabularySetWorker(action: PayloadAction<UpdateVocabularySetRequest>): SagaIterator {
    try {
        const set = yield call(vocabularyService.updateVocabularySet, action.payload);
        yield put(updateVocabularySetSuccess(set));
    } catch (error: any) {
        yield put(updateVocabularySetFailure(error.response?.data?.message || 'Failed to update vocabulary set'));
    }
}

function* deleteVocabularySetWorker(action: PayloadAction<string>): SagaIterator {
    try {
        yield call(vocabularyService.deleteVocabularySet, action.payload);
        yield put(deleteVocabularySetSuccess(action.payload));
    } catch (error: any) {
        yield put(deleteVocabularySetFailure(error.response?.data?.message || 'Failed to delete vocabulary set'));
    }
}

// Study Sessions Worker Sagas
function* createStudySessionWorker(action: PayloadAction<CreateStudySessionRequest>): SagaIterator {
    try {
        const session = yield call(vocabularyService.createStudySession, action.payload);
        yield put(createStudySessionSuccess(session));
    } catch (error: any) {
        yield put(createStudySessionFailure(error.response?.data?.message || 'Failed to create study session'));
    }
}

function* updateStudySessionWorker(action: PayloadAction<UpdateStudySessionRequest>): SagaIterator {
    try {
        const session = yield call(vocabularyService.updateStudySession, action.payload);
        yield put(updateStudySessionSuccess(session));
    } catch (error: any) {
        yield put(updateStudySessionFailure(error.response?.data?.message || 'Failed to update study session'));
    }
}

function* fetchStudySessionsWorker(action: PayloadAction<{ userId?: number; groupId?: string }>): SagaIterator {
    try {
        const { userId, groupId } = action.payload;
        const sessions = yield call(vocabularyService.getStudySessions, userId, groupId);
        yield put(fetchStudySessionsSuccess(sessions));
    } catch (error: any) {
        yield put(fetchStudySessionsFailure(error.response?.data?.message || 'Failed to fetch study sessions'));
    }
}

function* completeStudySessionWorker(action: PayloadAction<{ sessionId: string; completionData?: CompleteStudySessionRequest }>): SagaIterator {
    try {
        const { sessionId, completionData } = action.payload;
        const completionResponse = yield call(vocabularyService.completeStudySession, sessionId, completionData);
        yield put(completeStudySessionSuccess(completionResponse));
    } catch (error: any) {
        yield put(completeStudySessionFailure(error.response?.data?.message || 'Failed to complete study session'));
    }
}

function* submitVocabularyAnswerWorker(action: PayloadAction<SubmitVocabularyAnswerRequest>): SagaIterator {
    try {
        const attempt = yield call(vocabularyService.submitVocabularyAnswer, action.payload);
        yield put(submitVocabularyAnswerSuccess(attempt));
    } catch (error: any) {
        yield put(submitVocabularyAnswerFailure(error.response?.data?.message || 'Failed to submit vocabulary answer'));
    }
}

// Group Collections Worker Sagas
function* fetchGroupCollectionsWorker(action: PayloadAction<PaginationParams | undefined>): SagaIterator {
    try {
        const collections = yield call(vocabularyService.getGroupCollections, action.payload);
        yield put(fetchGroupCollectionsSuccess(collections));
    } catch (error: any) {
        yield put(fetchGroupCollectionsFailure(error.response?.data?.message || 'Failed to fetch group collections'));
    }
}

function* createGroupCollectionWorker(action: PayloadAction<CreateGroupCollectionRequest>): SagaIterator {
    try {
        const collection = yield call(vocabularyService.createGroupCollection, action.payload);
        yield put(createGroupCollectionSuccess(collection));
    } catch (error: any) {
        yield put(createGroupCollectionFailure(error.response?.data?.message || 'Failed to create group collection'));
    }
}

function* updateGroupCollectionWorker(action: PayloadAction<UpdateGroupCollectionRequest>): SagaIterator {
    try {
        const collection = yield call(vocabularyService.updateGroupCollection, action.payload);
        yield put(updateGroupCollectionSuccess(collection));
    } catch (error: any) {
        yield put(updateGroupCollectionFailure(error.response?.data?.message || 'Failed to update group collection'));
    }
}

function* deleteGroupCollectionWorker(action: PayloadAction<string>): SagaIterator {
    try {
        yield call(vocabularyService.deleteGroupCollection, action.payload);
        yield put(deleteGroupCollectionSuccess(action.payload));
    } catch (error: any) {
        yield put(deleteGroupCollectionFailure(error.response?.data?.message || 'Failed to delete group collection'));
    }
}

// Legacy Flashcard Groups Worker Sagas (for backward compatibility)
function* fetchFlashcardGroupsWorker(
    action: PayloadAction<{ filters?: FlashcardGroupFilters; pagination?: PaginationParams }>,
): SagaIterator {
    try {
        const { filters, pagination } = action.payload;
        const response = yield call(vocabularyService.getFlashcardGroups, filters, pagination);
        yield put(fetchFlashcardGroupsSuccess(response));
    } catch (error: any) {
        yield put(fetchFlashcardGroupsFailure(error.response?.data?.message || 'Failed to fetch flashcard groups'));
    }
}

function* fetchFlashcardGroupByIdWorker(action: PayloadAction<number>): SagaIterator {
    try {
        // For legacy compatibility, we need to handle this differently
        // Since the new backend uses UUIDs, we'll need to convert or handle appropriately
        console.warn('Legacy flashcard group access - consider migrating to vocabulary groups');
        yield put(fetchFlashcardGroupByIdFailure('Legacy flashcard group access not supported with new backend'));
    } catch (error: any) {
        yield put(fetchFlashcardGroupByIdFailure(error.response?.data?.message || 'Failed to fetch flashcard group'));
    }
}

function* createFlashcardGroupWorker(action: PayloadAction<CreateFlashcardGroupRequest>): SagaIterator {
    try {
        // Convert legacy format to new vocabulary group format
        const vocabularyGroupData: CreateVocabularyGroupRequest = {
            name: action.payload.name,
            description: action.payload.description,
            color: action.payload.color,
            icon: action.payload.icon,
            vocabularyIds: action.payload.vocabularyIds,
            privacy: 'private', // default for legacy groups
        };

        const group = yield call(vocabularyService.createVocabularyGroup, vocabularyGroupData);

        // Convert back to legacy format for compatibility
        const legacyGroup = {
            id: parseInt(group.id, 16), // Convert UUID to number (may lose precision)
            name: group.name,
            description: group.description,
            color: group.color,
            icon: group.icon,
            isDefault: group.is_default,
            createdAt: group.created_at,
            updatedAt: group.updated_at,
            vocabularyIds: action.payload.vocabularyIds || [],
            settings: {
                studyOrder: group.study_order,
                showPronunciation: group.show_pronunciation,
                showExample: group.show_example,
                autoAdvance: group.auto_advance,
                reviewMode: group.review_mode,
            },
        };

        yield put(createFlashcardGroupSuccess(legacyGroup));
    } catch (error: any) {
        yield put(createFlashcardGroupFailure(error.response?.data?.message || 'Failed to create flashcard group'));
    }
}

function* updateFlashcardGroupWorker(action: PayloadAction<UpdateFlashcardGroupRequest>): SagaIterator {
    try {
        console.warn('Legacy flashcard group update - consider migrating to vocabulary groups');
        yield put(updateFlashcardGroupFailure('Legacy flashcard group update not supported with new backend'));
    } catch (error: any) {
        yield put(updateFlashcardGroupFailure(error.response?.data?.message || 'Failed to update flashcard group'));
    }
}

function* deleteFlashcardGroupWorker(action: PayloadAction<number>): SagaIterator {
    try {
        console.warn('Legacy flashcard group delete - consider migrating to vocabulary groups');
        yield put(deleteFlashcardGroupFailure('Legacy flashcard group delete not supported with new backend'));
    } catch (error: any) {
        yield put(deleteFlashcardGroupFailure(error.response?.data?.message || 'Failed to delete flashcard group'));
    }
}

function* addWordsToGroupWorker(action: PayloadAction<{ groupId: number; wordIds: number[] }>): SagaIterator {
    try {
        console.warn('Legacy add words to group - consider migrating to vocabulary groups');
        yield put(addWordsToGroupFailure('Legacy add words to group not supported with new backend'));
    } catch (error: any) {
        yield put(addWordsToGroupFailure(error.response?.data?.message || 'Failed to add words to group'));
    }
}

function* removeWordsFromGroupWorker(action: PayloadAction<{ groupId: number; wordIds: number[] }>): SagaIterator {
    try {
        console.warn('Legacy remove words from group - consider migrating to vocabulary groups');
        yield put(removeWordsFromGroupFailure('Legacy remove words from group not supported with new backend'));
    } catch (error: any) {
        yield put(removeWordsFromGroupFailure(error.response?.data?.message || 'Failed to remove words from group'));
    }
}

function* fetchWordsInGroupWorker(
    action: PayloadAction<{ groupId: number; pagination?: PaginationParams }>,
): SagaIterator {
    try {
        console.warn('Legacy fetch words in group - consider migrating to vocabulary groups');
        yield put(fetchWordsInGroupFailure('Legacy fetch words in group not supported with new backend'));
    } catch (error: any) {
        yield put(fetchWordsInGroupFailure(error.response?.data?.message || 'Failed to fetch words in group'));
    }
}

// Practice Worker Sagas (placeholders for future implementation)
function* practiceWordWorker(action: PayloadAction<{ wordId: string; type: string }>): SagaIterator {
    try {
        // Mock implementation - replace with actual practice logic
        const result: PracticeResult = {
            wordId: action.payload.wordId,
            correct: Math.random() > 0.5,
            timeSpent: Math.floor(Math.random() * 30) + 10,
            difficulty: 'intermediate',
            practiceType: action.payload.type,
        };
        yield put(practiceWordSuccess(result));
    } catch (error: any) {
        yield put(practiceWordFailure(error.response?.data?.message || 'Failed to practice word'));
    }
}

function* submitPracticeResultWorker(
    action: PayloadAction<{ wordId: string; result: Omit<PracticeResult, 'wordId'> }>,
): SagaIterator {
    try {
        const result: PracticeResult = {
            wordId: action.payload.wordId,
            ...action.payload.result,
        };
        yield put(submitPracticeResultSuccess(result));
    } catch (error: any) {
        yield put(submitPracticeResultFailure(error.response?.data?.message || 'Failed to submit practice result'));
    }
}

function* fetchPracticeHistoryWorker(
    action: PayloadAction<{ wordId?: string; pagination?: PaginationParams }>,
): SagaIterator {
    try {
        // Mock implementation - replace with actual API call
        const response = {
            sessions: [],
            total: 0,
            page: 1,
            totalPages: 1,
        };
        yield put(fetchPracticeHistorySuccess(response));
    } catch (error: any) {
        yield put(fetchPracticeHistoryFailure(error.response?.data?.message || 'Failed to fetch practice history'));
    }
}

// Vocabulary Categories Worker Sagas
function* fetchVocabularyCategoriesWorker(): SagaIterator {
    try {
        const categories = yield call(vocabularyService.getVocabularyCategories);
        yield put(fetchVocabularyCategoriesSuccess(categories));
    } catch (error: any) {
        yield put(
            fetchVocabularyCategoriesFailure(error.response?.data?.message || 'Failed to fetch vocabulary categories'),
        );
    }
}

// === ENHANCED WORD SAVING WORKERS ===
function* fetchSavedWordsWorker(
    action: PayloadAction<{ filters?: SavedWordFilters; pagination?: PaginationParams }>,
): SagaIterator {
    try {
        const { filters, pagination } = action.payload;
        const response = yield call(vocabularyService.getSavedWords, filters, pagination);
        yield put(fetchSavedWordsSuccess(response));
    } catch (error: any) {
        yield put(fetchSavedWordsFailure(error.response?.data?.message || 'Failed to fetch saved words'));
    }
}

function* saveWordWorker(action: PayloadAction<CreateSavedWordRequest>): SagaIterator {
    try {
        const savedWord = yield call(vocabularyService.saveWord, action.payload);
        yield put(saveWordSuccess(savedWord));
    } catch (error: any) {
        yield put(saveWordFailure(error.response?.data?.message || 'Failed to save word'));
    }
}

function* updateSavedWordWorker(action: PayloadAction<UpdateSavedWordRequest>): SagaIterator {
    try {
        const updatedWord = yield call(vocabularyService.updateSavedWord, action.payload);
        yield put(updateSavedWordSuccess(updatedWord));
    } catch (error: any) {
        yield put(updateSavedWordFailure(error.response?.data?.message || 'Failed to update saved word'));
    }
}

function* removeSavedWordWorker(action: PayloadAction<string>): SagaIterator {
    try {
        yield call(vocabularyService.removeSavedWord, action.payload);
        yield put(removeSavedWordSuccess(action.payload));
    } catch (error: any) {
        yield put(removeSavedWordFailure(error.response?.data?.message || 'Failed to remove saved word'));
    }
}

function* bulkSaveWordsWorker(action: PayloadAction<{ wordIds: string[]; groupId?: string }>): SagaIterator {
    try {
        const { wordIds, groupId } = action.payload;
        const savedWords = yield call(vocabularyService.bulkSaveWords, wordIds, groupId);
        yield put(bulkSaveWordsSuccess(savedWords));
    } catch (error: any) {
        yield put(bulkSaveWordsFailure(error.response?.data?.message || 'Failed to bulk save words'));
    }
}

function* bulkRemoveSavedWordsWorker(action: PayloadAction<string[]>): SagaIterator {
    try {
        yield call(vocabularyService.bulkRemoveSavedWords, action.payload);
        yield put(bulkRemoveSavedWordsSuccess(action.payload));
    } catch (error: any) {
        yield put(bulkRemoveSavedWordsFailure(error.response?.data?.message || 'Failed to bulk remove saved words'));
    }
}

// === MINIGAME SESSION WORKERS ===
function* createMinigameSessionWorker(action: PayloadAction<CreateMinigameSessionRequest>): SagaIterator {
    try {
        const session = yield call(vocabularyService.createMinigameSession, action.payload);
        yield put(createMinigameSessionSuccess(session));
    } catch (error: any) {
        yield put(createMinigameSessionFailure(error.response?.data?.message || 'Failed to create minigame session'));
    }
}

function* updateMinigameSessionWorker(action: PayloadAction<UpdateMinigameSessionRequest>): SagaIterator {
    try {
        const session = yield call(vocabularyService.updateMinigameSession, action.payload);
        yield put(updateMinigameSessionSuccess(session));
    } catch (error: any) {
        yield put(updateMinigameSessionFailure(error.response?.data?.message || 'Failed to update minigame session'));
    }
}

function* fetchMinigameSessionsWorker(
    action: PayloadAction<{
        gameType?: MinigameSession['gameType'];
        pagination?: PaginationParams;
    }>,
): SagaIterator {
    try {
        const { gameType, pagination } = action.payload;
        const response = yield call(vocabularyService.getMinigameSessions, gameType, pagination);
        yield put(fetchMinigameSessionsSuccess(response));
    } catch (error: any) {
        yield put(fetchMinigameSessionsFailure(error.response?.data?.message || 'Failed to fetch minigame sessions'));
    }
}

function* fetchMinigameStatsWorker(action: PayloadAction<MinigameSession['gameType'] | undefined>): SagaIterator {
    try {
        const stats = yield call(vocabularyService.getMinigameStats, action.payload);
        yield put(fetchMinigameStatsSuccess(stats));
    } catch (error: any) {
        yield put(fetchMinigameStatsFailure(error.response?.data?.message || 'Failed to fetch minigame stats'));
    }
}

function* generateMinigameWordsWorker(
    action: PayloadAction<{
        gameType: MinigameSession['gameType'];
        difficulty: 'easy' | 'medium' | 'hard';
        count?: number;
        groupId?: string;
    }>,
): SagaIterator {
    try {
        const { gameType, difficulty, count, groupId } = action.payload;
        const words = yield call(vocabularyService.generateMinigameWords, gameType, difficulty, count, groupId);
        yield put(generateMinigameWordsSuccess(words));
    } catch (error: any) {
        yield put(generateMinigameWordsFailure(error.response?.data?.message || 'Failed to generate minigame words'));
    }
}

// === PROGRESS TRACKING WORKERS ===
function* updateStudyProgressWorker(action: PayloadAction<UpdateStudyProgressRequest>): SagaIterator {
    try {
        const progress = yield call(vocabularyService.updateStudyProgress, action.payload);
        yield put(updateStudyProgressSuccess(progress));
    } catch (error: any) {
        yield put(updateStudyProgressFailure(error.response?.data?.message || 'Failed to update study progress'));
    }
}

function* fetchStudyProgressWorker(action: PayloadAction<string>): SagaIterator {
    try {
        const progress = yield call(vocabularyService.getStudyProgress, action.payload);
        yield put(fetchStudyProgressSuccess(progress));
    } catch (error: any) {
        yield put(fetchStudyProgressFailure(error.response?.data?.message || 'Failed to fetch study progress'));
    }
}

function* fetchUserStudyProgressWorker(
    action: PayloadAction<{
        masteryLevel?: number;
        lastStudiedBefore?: string;
    }>,
): SagaIterator {
    try {
        const progress = yield call(vocabularyService.getUserStudyProgress, action.payload);
        yield put(fetchUserStudyProgressSuccess(progress));
    } catch (error: any) {
        yield put(
            fetchUserStudyProgressFailure(error.response?.data?.message || 'Failed to fetch user study progress'),
        );
    }
}

function* fetchWordsForReviewWorker(action: PayloadAction<number>): SagaIterator {
    try {
        const words = yield call(vocabularyService.getWordsForReview, action.payload);
        yield put(fetchWordsForReviewSuccess(words));
    } catch (error: any) {
        yield put(fetchWordsForReviewFailure(error.response?.data?.message || 'Failed to fetch words for review'));
    }
}

// === ENHANCED GROUP MANAGEMENT WORKERS ===
function* reorderWordsInGroupWorker(
    action: PayloadAction<{
        groupId: string;
        wordOrders: { wordId: string; order: number }[];
    }>,
): SagaIterator {
    try {
        const { groupId, wordOrders } = action.payload;
        yield call(vocabularyService.reorderWordsInGroup, groupId, wordOrders);
        yield put(reorderWordsInGroupSuccess(groupId));
    } catch (error: any) {
        yield put(reorderWordsInGroupFailure(error.response?.data?.message || 'Failed to reorder words in group'));
    }
}

function* moveWordsBetweenGroupsWorker(
    action: PayloadAction<{
        sourceGroupId: string;
        targetGroupId: string;
        wordIds: string[];
    }>,
): SagaIterator {
    try {
        const { sourceGroupId, targetGroupId, wordIds } = action.payload;
        yield call(vocabularyService.moveWordsBetweenGroups, sourceGroupId, targetGroupId, wordIds);
        yield put(moveWordsBetweenGroupsSuccess({ sourceGroupId, targetGroupId, wordIds }));
    } catch (error: any) {
        yield put(
            moveWordsBetweenGroupsFailure(error.response?.data?.message || 'Failed to move words between groups'),
        );
    }
}

function* duplicateGroupWorker(action: PayloadAction<{ groupId: string; newName: string }>): SagaIterator {
    try {
        const { groupId, newName } = action.payload;
        const duplicatedGroup = yield call(vocabularyService.duplicateGroup, groupId, newName);
        yield put(duplicateGroupSuccess(duplicatedGroup));
    } catch (error: any) {
        yield put(duplicateGroupFailure(error.response?.data?.message || 'Failed to duplicate group'));
    }
}

// === SESSION INTEGRATION WORKERS ===
function* linkWordsToSessionWorker(action: PayloadAction<{ sessionId: string; wordIds: string[] }>): SagaIterator {
    try {
        const { sessionId, wordIds } = action.payload;
        yield call(vocabularyService.linkWordsToSession, sessionId, wordIds);
        yield put(linkWordsToSessionSuccess({ sessionId, wordIds }));
    } catch (error: any) {
        yield put(linkWordsToSessionFailure(error.response?.data?.message || 'Failed to link words to session'));
    }
}

function* fetchSessionVocabularyWorker(action: PayloadAction<string>): SagaIterator {
    try {
        const words = yield call(vocabularyService.getSessionVocabulary, action.payload);
        yield put(fetchSessionVocabularySuccess(words));
    } catch (error: any) {
        yield put(fetchSessionVocabularyFailure(error.response?.data?.message || 'Failed to fetch session vocabulary'));
    }
}

function* updateSessionVocabularyProgressWorker(
    action: PayloadAction<{
        sessionId: string;
        wordId: string;
        progress: {
            isCorrect: boolean;
            timeSpent: number;
            attempts: number;
        };
    }>,
): SagaIterator {
    try {
        const { sessionId, wordId, progress } = action.payload;
        yield call(vocabularyService.updateSessionVocabularyProgress, sessionId, wordId, progress);
        yield put(updateSessionVocabularyProgressSuccess({ sessionId, wordId, progress }));
    } catch (error: any) {
        yield put(
            updateSessionVocabularyProgressFailure(
                error.response?.data?.message || 'Failed to update session vocabulary progress',
            ),
        );
    }
}

function* fetchTranscriptDataWorker(action: PayloadAction<{ id: string; type: string }>): SagaIterator {
    try {
        const transcriptData = yield call(vocabularyService.getTranscriptData, action.payload.id, action.payload.type);
        yield put(fetchTranscriptDataSuccess(transcriptData));
    } catch (error: any) {
        yield put(fetchTranscriptDataFailure(error.response?.data?.message || 'Failed to fetch transcript data'));
    }
}

// Root Vocabulary Saga
export default function* vocabularySaga(): SagaIterator {
    // Words
    yield takeLatest(fetchWordsRequest.type, fetchWordsWorker);
    yield takeLatest(fetchWordByIdRequest.type, fetchWordByIdWorker);
    yield takeEvery(createWordRequest.type, createWordWorker);
    yield takeEvery(createBulkWordsRequest.type, createBulkWordsWorker);
    yield takeEvery(updateWordRequest.type, updateWordWorker);
    yield takeEvery(deleteWordRequest.type, deleteWordWorker);
    yield takeEvery(toggleFavoriteRequest.type, toggleFavoriteWorker);
    yield takeEvery(toggleBookmarkRequest.type, toggleBookmarkWorker);
    yield takeLatest(fetchFavoriteWordsRequest.type, fetchFavoriteWordsWorker);
    yield takeLatest(fetchBookmarkedWordsRequest.type, fetchBookmarkedWordsWorker);

    // Categories
    yield takeLatest(fetchCategoriesRequest.type, fetchCategoriesWorker);

    // Enhanced Vocabulary Groups
    yield takeLatest(fetchVocabularyGroupsRequest.type, fetchVocabularyGroupsWorker);
    yield takeLatest(fetchVocabularyGroupByIdRequest.type, fetchVocabularyGroupByIdWorker);
    yield takeLatest(createVocabularyGroupRequest.type, createVocabularyGroupWorker);
    yield takeLatest(updateVocabularyGroupRequest.type, updateVocabularyGroupWorker);
    yield takeLatest(deleteVocabularyGroupRequest.type, deleteVocabularyGroupWorker);
    yield takeEvery(addWordsToVocabularyGroupRequest.type, addWordsToVocabularyGroupWorker);
    yield takeEvery(removeWordsFromVocabularyGroupRequest.type, removeWordsFromVocabularyGroupWorker);
    yield takeLatest(fetchWordsInVocabularyGroupRequest.type, fetchWordsInVocabularyGroupWorker);

    // Group Sharing
    yield takeEvery(shareVocabularyGroupRequest.type, shareVocabularyGroupWorker);
    yield takeLatest(fetchGroupSharesRequest.type, fetchGroupSharesWorker);

    // Vocabulary Sets
    yield takeLatest(fetchVocabularySetsRequest.type, fetchVocabularySetsWorker);
    yield takeLatest(fetchVocabularySetByIdRequest.type, fetchVocabularySetByIdWorker);
    yield takeEvery(createVocabularySetRequest.type, createVocabularySetWorker);
    yield takeEvery(updateVocabularySetRequest.type, updateVocabularySetWorker);
    yield takeEvery(deleteVocabularySetRequest.type, deleteVocabularySetWorker);

    // Study Sessions
    yield takeEvery(createStudySessionRequest.type, createStudySessionWorker);
    yield takeEvery(updateStudySessionRequest.type, updateStudySessionWorker);
    yield takeLatest(fetchStudySessionsRequest.type, fetchStudySessionsWorker);
    yield takeEvery(completeStudySessionRequest.type, completeStudySessionWorker);
    yield takeEvery(submitVocabularyAnswerRequest.type, submitVocabularyAnswerWorker);

    // Group Collections
    yield takeLatest(fetchGroupCollectionsRequest.type, fetchGroupCollectionsWorker);
    yield takeEvery(createGroupCollectionRequest.type, createGroupCollectionWorker);
    yield takeEvery(updateGroupCollectionRequest.type, updateGroupCollectionWorker);
    yield takeEvery(deleteGroupCollectionRequest.type, deleteGroupCollectionWorker);

    // Legacy Flashcard Groups (for backward compatibility)
    yield takeLatest(fetchFlashcardGroupsRequest.type, fetchFlashcardGroupsWorker);
    yield takeLatest(fetchFlashcardGroupByIdRequest.type, fetchFlashcardGroupByIdWorker);
    yield takeEvery(createFlashcardGroupRequest.type, createFlashcardGroupWorker);
    yield takeEvery(updateFlashcardGroupRequest.type, updateFlashcardGroupWorker);
    yield takeEvery(deleteFlashcardGroupRequest.type, deleteFlashcardGroupWorker);
    yield takeEvery(addWordsToGroupRequest.type, addWordsToGroupWorker);
    yield takeEvery(removeWordsFromGroupRequest.type, removeWordsFromGroupWorker);
    yield takeLatest(fetchWordsInGroupRequest.type, fetchWordsInGroupWorker);

    // Practice
    yield takeEvery(practiceWordRequest.type, practiceWordWorker);
    yield takeEvery(submitPracticeResultRequest.type, submitPracticeResultWorker);
    yield takeLatest(fetchPracticeHistoryRequest.type, fetchPracticeHistoryWorker);

    // === ENHANCED WORD SAVING ===
    yield takeLatest(fetchSavedWordsRequest.type, fetchSavedWordsWorker);
    yield takeEvery(saveWordRequest.type, saveWordWorker);
    yield takeEvery(updateSavedWordRequest.type, updateSavedWordWorker);
    yield takeEvery(removeSavedWordRequest.type, removeSavedWordWorker);
    yield takeEvery(bulkSaveWordsRequest.type, bulkSaveWordsWorker);
    yield takeEvery(bulkRemoveSavedWordsRequest.type, bulkRemoveSavedWordsWorker);

    // === MINIGAME SESSIONS ===
    yield takeEvery(createMinigameSessionRequest.type, createMinigameSessionWorker);
    yield takeEvery(updateMinigameSessionRequest.type, updateMinigameSessionWorker);
    yield takeLatest(fetchMinigameSessionsRequest.type, fetchMinigameSessionsWorker);
    yield takeLatest(fetchMinigameStatsRequest.type, fetchMinigameStatsWorker);
    yield takeEvery(generateMinigameWordsRequest.type, generateMinigameWordsWorker);

    // === PROGRESS TRACKING ===
    yield takeEvery(updateStudyProgressRequest.type, updateStudyProgressWorker);
    yield takeLatest(fetchStudyProgressRequest.type, fetchStudyProgressWorker);
    yield takeLatest(fetchUserStudyProgressRequest.type, fetchUserStudyProgressWorker);
    yield takeLatest(fetchWordsForReviewRequest.type, fetchWordsForReviewWorker);

    // === ENHANCED GROUP MANAGEMENT ===
    yield takeEvery(reorderWordsInGroupRequest.type, reorderWordsInGroupWorker);
    yield takeEvery(moveWordsBetweenGroupsRequest.type, moveWordsBetweenGroupsWorker);
    yield takeEvery(duplicateGroupRequest.type, duplicateGroupWorker);

    // === SESSION INTEGRATION ===
    yield takeEvery(linkWordsToSessionRequest.type, linkWordsToSessionWorker);
    yield takeLatest(fetchSessionVocabularyRequest.type, fetchSessionVocabularyWorker);
    yield takeEvery(updateSessionVocabularyProgressRequest.type, updateSessionVocabularyProgressWorker);

    // Vocabulary Categories
    yield takeLatest(fetchVocabularyCategoriesRequest.type, fetchVocabularyCategoriesWorker);

    // Word timing
    yield takeLatest(fetchTranscriptDataRequest.type, fetchTranscriptDataWorker);
}
